import React from 'react';
import { Box, Drawer, List, Typography, useMediaQuery } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAppTheme } from '../../theme';
import { approvalService } from '../../services/workspaceService';
import {
  NAV_SECTIONS,
  useNavItems,
  MenuItemWithRole,
} from '../shell/navConfig';
import {
  NAVY,
  RAIL_WIDTH,
  RAIL_EXPANDED_WIDTH,
  eyebrowSx,
  motion,
} from '../shell/shellTokens';
import RailBrand from '../shell/RailBrand';
import RailItem from '../shell/RailItem';
import RailSectionLabel from '../shell/RailSectionLabel';
import { RailInteractionProps } from '../shell/useRailState';

interface SidebarProps {
  /** Drawer is mounted/visible at all (hamburger on desktop, overlay on mobile). */
  open: boolean;
  handleDrawerToggle: () => void;
  /** Locked open by the user — suppresses auto-collapse. Persisted. */
  pinned: boolean;
  onTogglePin: () => void;
  /** Showing labels: pinned, hovered, or reached by keyboard. Owned by AppLayout. */
  expanded: boolean;
  /** Hover/focus handlers for the drawer paper — see `useRailState`. */
  railProps: RailInteractionProps;
}

/**
 * The navigation rail.
 *
 * Collapsed to a 76px icon rail by default; expands to 280px on hover, on
 * keyboard focus, or permanently when pinned. Expanding PUSHES the workspace
 * across — the expansion state lives in AppLayout precisely so the nav column,
 * the AppBar and this drawer paper all animate off one number and the page is
 * never covered by the menu.
 *
 * Collapsed, every row keeps its icon and gains a tooltip; nothing is ever a
 * blank strip.
 *
 * The rail's palette is fixed navy (see shellTokens) and deliberately does NOT
 * follow the tenant's theme preset.
 */
const Sidebar: React.FC<SidebarProps> = ({
  open,
  handleDrawerToggle,
  pinned,
  onTogglePin,
  expanded,
  railProps,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { brand } = useAppTheme();
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  const menuItems = useNavItems();
  const [expandedItems, setExpandedItems] = React.useState<string[]>([]);

  // Approvals badge. Same query key as the header's approvals button, so React
  // Query serves both surfaces from one cached 60s poll rather than doubling
  // the request rate.
  const { data: approvals } = useQuery<{ total: number }>({
    queryKey: ['approvals', 'summary'],
    queryFn: () => approvalService.summary(),
    refetchInterval: 60_000,
    retry: false,
  });
  const approvalTotal = approvals?.total ?? 0;

  React.useEffect(() => {
    // Auto-expand a parent when the active route is under its path OR is one of
    // its (possibly relocated) sub-items — a payroll item moved under Finance
    // must expand Finance, whose path /finance the /hr/payroll route doesn't
    // share.
    menuItems.forEach((item) => {
      if (
        item.subItems &&
        (location.pathname.startsWith(item.path) ||
          item.subItems.some((s) => location.pathname === s.path))
      ) {
        setExpandedItems((prev) => (prev.includes(item.text) ? prev : [...prev, item.text]));
      }
    });
  }, [location.pathname, menuItems]);

  const handleNavigation = (path: string) => {
    navigate(path);
    // Matches AppLayout's `sm` breakpoint. This previously hardcoded 900px,
    // so between 600 and 900 the persistent desktop drawer closed itself on
    // every navigation.
    if (window.innerWidth < 600) {
      handleDrawerToggle();
    }
  };

  const handleExpandClick = (itemText: string) => {
    setExpandedItems((prev) =>
      prev.includes(itemText) ? prev.filter((i) => i !== itemText) : [...prev, itemText]
    );
  };

  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/');

  const badgeFor = (item: MenuItemWithRole) =>
    item.path === '/approvals/pending' ? approvalTotal : undefined;

  /**
   * One render for both drawers. `isExpanded` is forced true inside the mobile
   * overlay — a modal drawer that the user deliberately opened has no reason to
   * hide its labels.
   */
  const renderRail = (isExpanded: boolean, showPin: boolean) => (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Masked grid — the same motif as the auth panel, faded out before it
          reaches the nav items so it never competes with the labels. */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 120% 40% at 50% 0%, #000 0%, transparent 75%)',
          WebkitMaskImage: 'radial-gradient(ellipse 120% 40% at 50% 0%, #000 0%, transparent 75%)',
          pointerEvents: 'none',
        }}
      />

      <RailBrand
        expanded={isExpanded}
        pinned={pinned}
        onTogglePin={onTogglePin}
        reducedMotion={reducedMotion}
        showPin={showPin}
      />

      <List
        sx={{
          position: 'relative',
          px: 1,
          pb: 2,
          flexGrow: 1,
          overflowY: 'auto',
          // The rail always scrolls vertically (24 modules exceed most
          // viewports) but must never scroll horizontally, or the collapsing
          // labels would produce a phantom scrollbar mid-animation.
          overflowX: 'hidden',
          // Thin, self-coloured scrollbar. The platform default is a bright
          // slab on a near-black panel.
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(255,255,255,0.18) transparent',
          '&::-webkit-scrollbar': { width: 6 },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255,255,255,0.18)',
            borderRadius: 3,
          },
          '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
        }}
      >
        {/* Sections render in the IA's order; items keep their original order
            WITHIN a section, and the items themselves come straight from
            useNavItems() — the one role/entitlement-filtered source. */}
        {NAV_SECTIONS.map(({ id, label }) => {
          const sectionItems = menuItems.filter((item) => item.section === id);
          // A section whose every item is role- or module-gated away prints
          // nothing at all, header included.
          if (sectionItems.length === 0) return null;

          return (
            <React.Fragment key={id}>
              {label && (
                <RailSectionLabel
                  label={label}
                  expanded={isExpanded}
                  reducedMotion={reducedMotion}
                />
              )}
              {sectionItems.map((item) => (
                <RailItem
                  key={item.text}
                  item={item}
                  expanded={isExpanded}
                  open={expandedItems.includes(item.text)}
                  active={isActive(item.path)}
                  activeSubPath={location.pathname}
                  reducedMotion={reducedMotion}
                  onToggle={handleExpandClick}
                  onNavigate={handleNavigation}
                  badge={badgeFor(item)}
                />
              ))}
            </React.Fragment>
          );
        })}
      </List>

      {/* Footer */}
      <Box
        sx={{
          position: 'relative',
          px: isExpanded ? 2.5 : 1,
          py: 1.75,
          borderTop: `1px solid ${NAVY.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: isExpanded ? 'space-between' : 'center',
          gap: 1,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
        }}
      >
        {isExpanded && (
          <Typography sx={{ ...eyebrowSx, color: NAVY.textFaint }}>{brand.shortName}</Typography>
        )}
        <Typography
          sx={{
            fontSize: '0.6875rem',
            color: NAVY.textFaint,
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          v{brand.version}
        </Typography>
      </Box>
    </Box>
  );

  /** Paper styling shared by both drawers. Navy, fixed, never theme-derived. */
  const paperBase = {
    boxSizing: 'border-box' as const,
    background: NAVY.bgGradient,
    backgroundColor: NAVY.bg,
    color: NAVY.text,
    borderRight: `1px solid ${NAVY.border}`,
    overflowX: 'hidden' as const,
  };

  return (
    <Box
      component="nav"
      sx={{
        // The layout spacer: this is what actually pushes the workspace. It
        // tracks the rail's current width, so the content edge and the rail
        // edge are the same edge.
        width: { sm: open ? (expanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH) : 0 },
        flexShrink: 0,
        transition: motion(reducedMotion, 'width .22s cubic-bezier(.4,0,.2,1)'),
      }}
      aria-label="Main navigation"
    >
      {/* Mobile drawer — a modal overlay, always full width, never collapsed. */}
      <Drawer
        variant="temporary"
        open={open}
        onClose={handleDrawerToggle}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': { ...paperBase, width: RAIL_EXPANDED_WIDTH, boxShadow: 'none' },
        }}
      >
        {renderRail(true, false)}
      </Drawer>

      {/* Desktop rail */}
      <Drawer
        variant="persistent"
        open={open}
        // onMouseEnter/Leave on the Drawer ROOT would also fire over the
        // (absent) backdrop; putting them on the paper keeps the trigger area
        // exactly the rail. onFocus/onBlur are React's bubbling
        // focusin/focusout — see useRailState for why only keyboard focus
        // counts.
        PaperProps={railProps}
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            ...paperBase,
            width: expanded ? RAIL_EXPANDED_WIDTH : RAIL_WIDTH,
            transition: motion(reducedMotion, 'width .22s cubic-bezier(.4,0,.2,1)'),
            // No shadow: the rail pushes the workspace rather than floating
            // over it, so there is nothing for it to cast onto. The 1px border
            // is the whole separation.
            boxShadow: 'none',
          },
        }}
      >
        {renderRail(expanded, true)}
      </Drawer>
    </Box>
  );
};

export default Sidebar;
